import { cn } from "@/lib/utils";
import * as React from "react";

/**
 * MetricBar — dotted progress bar matching the reference design.
 * Shows: LABEL ........... VALUE
 * The dots fill proportionally based on `percent`.
 */
function MetricBar({
  className,
  label,
  value,
  percent,
}: {
  className?: string;
  label: string;
  value: string;
  percent: number;
}) {
  const clampedPercent = Math.max(0, Math.min(100, percent));
  const totalDots = 12;
  const filledDots = Math.round((clampedPercent / 100) * totalDots);

  return (
    <div className={cn("flex items-center justify-between gap-3", className)}>
      <span className="text-xs font-semibold uppercase tracking-wider text-foreground font-mono shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-0.5 flex-1 justify-end">
        <div className="flex gap-[3px]">
          {Array.from({ length: totalDots }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "size-[5px] rounded-full",
                i < filledDots ? "bg-foreground" : "bg-border",
              )}
            />
          ))}
        </div>
        <span className="ml-2 text-xs font-medium text-muted-foreground font-mono tabular-nums w-8 text-right">
          {value}
        </span>
      </div>
    </div>
  );
}

/**
 * StatBlock — the stats panel from the reference (Total Requests: 3, Users: 2, etc.)
 */
function StatBlock({
  className,
  label,
  value,
  ...props
}: React.ComponentProps<"div"> & {
  label: string;
  value: string | number;
}) {
  return (
    <div className={cn("space-y-1", className)} {...props}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">{label}</p>
      <p className="text-xl font-semibold text-foreground tabular-nums">{String(value)}</p>
    </div>
  );
}

export { MetricBar, StatBlock };
